import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Statistic,
  Badge,
  Button,
  Empty,
  Popover,
  Timeline,
  Table,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  DollarOutlined,
  TeamOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PaperClipOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { workApprovalApis } from "../../../api/WorkApproval";
import { tasksApis } from "../../../api/NewTasks";
import {
  formatCurrency,
  resourceLineTotal,
  toolLineTotal,
} from "./timelogAmountUtils";

interface TimeLogQuickViewCardProps {
  timeLogData: any;
  onEdit?: () => void;
  showEditButton?: boolean;
  onRefresh?: () => void;
}

const secondsToHHMM = (totalSeconds: number) => {
  if (!totalSeconds || isNaN(totalSeconds)) return "00:00";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const to12HourTime = (time?: string): string => {
  if (!time || !time.includes(":")) return "--:--";
  const [h, m] = time.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${m} ${ampm}`;
};

const formatDate = (d: any) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

const getApprovalStatusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "approved") return "success";
  if (s === "rejected") return "error";
  if (s === "pending") return "warning";
  return "default";
};

const TimeLogQuickViewCard: FC<TimeLogQuickViewCardProps> = ({
  timeLogData,
  onEdit,
  showEditButton = true,
  onRefresh,
}) => {
  const { data: currentUser } = useSelector(
    (state: any) => state.currentUser
  );
  const [isApprovalProcessing, setIsApprovalProcessing] = useState(false);
  // materialId -> name, to label tool consumed-materials in the expandable row.
  const [materialNameMap, setMaterialNameMap] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    (async () => {
      try {
        const res = await tasksApis.fetchMachineryMaterialDropDown();
        if (res?.status) {
          const map: Record<number, string> = {};
          (res.data || []).forEach((m: any) => {
            map[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
          });
          setMaterialNameMap(map);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  if (!timeLogData) {
    return (
      <div style={{ padding: 40 }}>
        <Empty description="No time log data available" />
      </div>
    );
  }

  const totalSeconds = timeLogData.totalWorkHoursInSec || 0;
  const totalHours = secondsToHHMM(totalSeconds);
  const totalHoursDecimal = (totalSeconds / 3600).toFixed(2);

  const linkedEntity = timeLogData.taskName
    ? { type: "Task", name: timeLogData.taskName, id: timeLogData.taskId }
    : timeLogData.issueName
    ? { type: "Issue", name: timeLogData.issueName, id: timeLogData.issueId }
    : null;

  const approvalChain: any[] = Array.isArray(timeLogData.stateApprovals)
    ? timeLogData.stateApprovals
    : [];

  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";

  const isApprover =
    !!timeLogData.approvalLinkId &&
    (approvalChain.some(
      (a: any) => a.userId === currentUser?.id && a.status === "Pending"
    ) ||
      (isAdmin && approvalChain.some((a: any) => a.status === "Pending")));

  const isApproved =
    (timeLogData.approvalStatus || "").toLowerCase() === "approved";

  const canEditTimeLog = (() => {
    if (!currentUser) return false;
    if (currentUser.genericRole?.toLowerCase() === "admin") return true;
    if (
      timeLogData.taskId &&
      timeLogData.taskOwnerId &&
      String(currentUser.id) === String(timeLogData.taskOwnerId)
    )
      return true;
    if (timeLogData.issueId) {
      const issueOwnerId =
        timeLogData.issueOwnerId ||
        timeLogData.issueAssignedToId ||
        timeLogData.assignedTo;
      if (issueOwnerId && String(currentUser.id) === String(issueOwnerId))
        return true;
    }
    if (String(currentUser.id) === String(timeLogData.createdBy)) return true;
    return false;
  })();

  const showEdit = showEditButton && !!onEdit && canEditTimeLog && !isApproved;

  const handleApproveReject = async (
    action: "approve" | "reject",
    reason?: string
  ) => {
    if (!timeLogData?.approvalLinkId) return;
    setIsApprovalProcessing(true);
    try {
      const baseRequestData = {
        projectId: timeLogData.projectId,
        data: [
          {
            approvalLinkId: timeLogData.approvalLinkId,
            parentModule: timeLogData.moduleType,
          },
        ],
      };
      const response: any =
        action === "approve"
          ? await workApprovalApis.approveWorkItem(baseRequestData)
          : await workApprovalApis.rejectWorkItem({
              ...baseRequestData,
              reason: reason || "",
            });
      const { status, message: msg } = response || {};
      if (status) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.data?.message ||
            msg ||
            `Successfully ${action === "approve" ? "approved" : "rejected"} time log`,
          confirmButtonText: "OK",
        });
        onRefresh?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text:
            response?.data?.message ||
            response?.response?.data?.message ||
            msg ||
            `Failed to ${action} time log.`,
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          `Failed to ${action} time log.`,
        confirmButtonText: "OK",
      });
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const promptReject = async () => {
    const { value: reason } = await Swal.fire({
      title: "Reject Time Log",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Type your reason here...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) return "Please input your reason!";
        return null;
      },
    });
    if (reason) handleApproveReject("reject", reason);
  };

  const ApprovalHierarchyPopover = () => (
    <Popover
      trigger={["hover", "click"]}
      placement="right"
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      content={
        <Timeline className="approval-timeline">
          {approvalChain.map((approval: any, index: number) => {
            const status = approval.status;
            const isApproved = status === "Approved";
            const isRejected = status === "Rejected";
            return (
              <Timeline.Item
                key={index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                {approval?.userName}
                <div className="designation">{approval?.roleName}</div>
                {approval?.approvalStage && (
                  <div className="designation">{approval?.approvalStage}</div>
                )}
                {approval?.level && (
                  <div className="designation">Level: {approval?.level}</div>
                )}
                {status && status !== "Active" && (
                  <div className="designation">Status: {status}</div>
                )}
                {approval?.actionDate && (
                  <div className="designation">
                    {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );

  const resourceCount = (timeLogData.timeLogResources || []).length;
  const toolCount = (timeLogData.timeLogTools || []).length;
  const fileCount = (timeLogData.dms || []).length;

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header — title + actions in same row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4
            style={{
              margin: "0 0 8px 0",
              fontSize: 20,
              fontWeight: 600,
              color: "#1a1a2e",
              wordBreak: "break-word",
            }}
          >
            {linkedEntity?.name ||
              timeLogData.timeLogTitle ||
              `Time Log #${timeLogData.timeLogId}`}
          </h4>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {timeLogData.approvalStatus && (
              <Badge
                status={getApprovalStatusColor(timeLogData.approvalStatus)}
                text={
                  <span style={{ fontWeight: 500, fontSize: 13 }}>
                    {timeLogData.approvalStatus}
                  </span>
                }
              />
            )}
            {linkedEntity && (
              <Tag color={linkedEntity.type === "Task" ? "blue" : "purple"}>
                {linkedEntity.type}
              </Tag>
            )}
            {timeLogData.billingType && (
              <Tag
                color={
                  (timeLogData.billingType || "").toLowerCase() === "billable"
                    ? "green"
                    : "default"
                }
              >
                {(timeLogData.billingType || "").toLowerCase() === "billable"
                  ? "Billable"
                  : "Non-Billable"}
              </Tag>
            )}
            {timeLogData.shiftName && (
              <Tag color="cyan">{timeLogData.shiftName}</Tag>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isApprover && (
            <>
              <Button
                size="small"
                style={{
                  backgroundColor: "#d1f5ec",
                  color: "#107154",
                  borderColor: "#d1f5ec",
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={() => handleApproveReject("approve")}
              >
                Approve
              </Button>
              <Button
                size="small"
                style={{
                  backgroundColor: "#fbe3e4",
                  color: "#a94442",
                  borderColor: "#fbe3e4",
                  fontWeight: 500,
                }}
                loading={isApprovalProcessing}
                onClick={promptReject}
              >
                Reject
              </Button>
            </>
          )}
          {showEdit && (
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Hours / Time Period gradient card */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          borderRadius: 12,
          background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
          border: "none",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12}>
            <Statistic
              title={
                <span
                  style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}
                >
                  Total Hours Logged
                </span>
              }
              value={totalHours}
              suffix={
                <span
                  style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}
                >
                  ({totalHoursDecimal} hrs)
                </span>
              }
              valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 600 }}
            />
            <div
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {timeLogData.logDate
                ? dayjs(timeLogData.logDate).format("DD MMM YYYY")
                : "Date not set"}
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: 12,
                  marginBottom: 4,
                }}
              >
                Time Period
              </div>
              <div style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>
                {to12HourTime(timeLogData.loginTime)}
                <span
                  style={{
                    margin: "0 8px",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  →
                </span>
                {to12HourTime(timeLogData.logoutTime)}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Quick Stats */}
      {(resourceCount > 0 || toolCount > 0 || fileCount > 0) && (
        <Row gutter={12} style={{ marginBottom: 20 }}>
          {resourceCount > 0 && (
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  background: "#f0f9ff",
                  border: "1px solid #bae0ff",
                }}
                styles={{ body: { padding: "12px 16px" } }}
              >
                <Statistic
                  title={
                    <span style={{ fontSize: 12, color: "#0958d9" }}>
                      Resources
                    </span>
                  }
                  value={resourceCount}
                  valueStyle={{ color: "#0958d9", fontSize: 22 }}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
          )}
          {toolCount > 0 && (
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  background: "#fff7e6",
                  border: "1px solid #ffd591",
                }}
                styles={{ body: { padding: "12px 16px" } }}
              >
                <Statistic
                  title={
                    <span style={{ fontSize: 12, color: "#d46b08" }}>
                      Tools
                    </span>
                  }
                  value={toolCount}
                  valueStyle={{ color: "#d46b08", fontSize: 22 }}
                  prefix={<ToolOutlined />}
                />
              </Card>
            </Col>
          )}
          {fileCount > 0 && (
            <Col xs={24} sm={8}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  background: "#f9f0ff",
                  border: "1px solid #d3adf7",
                }}
                styles={{ body: { padding: "12px 16px" } }}
              >
                <Statistic
                  title={
                    <span style={{ fontSize: 12, color: "#722ed1" }}>
                      Files
                    </span>
                  }
                  value={fileCount}
                  valueStyle={{ color: "#722ed1", fontSize: 22 }}
                  prefix={<PaperClipOutlined />}
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Time Log Details */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Time Log Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        styles={{
          header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
          body: { padding: "12px 16px" },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          size="small"
          labelStyle={{
            fontWeight: 500,
            color: "#666",
            width: 150,
            whiteSpace: "nowrap",
          }}
          contentStyle={{ wordBreak: "break-word" }}
        >
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                User
              </>
            }
          >
            {timeLogData.createdByName || timeLogData.user || "—"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Log Date
              </>
            }
          >
            {formatDate(timeLogData.logDate)}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <NumberOutlined style={{ marginRight: 6 }} />
                Time Log ID
              </>
            }
          >
            <span style={{ fontFamily: "monospace", color: "#1a1a2e" }}>
              #{timeLogData.timeLogId}
            </span>
          </Descriptions.Item>
          {linkedEntity && (
            <Descriptions.Item
              label={
                <>
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Linked {linkedEntity.type}
                </>
              }
              span={2}
            >
              {linkedEntity.name}
            </Descriptions.Item>
          )}
          {timeLogData.projectName && (
            <Descriptions.Item
              label={
                <>
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Project
                </>
              }
            >
              {timeLogData.projectName}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <>
                <DollarOutlined style={{ marginRight: 6 }} />
                Billing Type
              </>
            }
          >
            <Tag
              color={
                (timeLogData.billingType || "").toLowerCase() === "billable"
                  ? "green"
                  : "default"
              }
            >
              {(timeLogData.billingType || "").toLowerCase() === "billable"
                ? "Billable"
                : "Non-Billable"}
            </Tag>
          </Descriptions.Item>
          {timeLogData.shiftName && (
            <Descriptions.Item
              label={
                <>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  Shift
                </>
              }
            >
              {timeLogData.shiftName}
            </Descriptions.Item>
          )}
          {timeLogData.taskCompletionPercentage !== undefined &&
            timeLogData.taskCompletionPercentage !== null && (
              <Descriptions.Item
                label={
                  <>
                    <CheckCircleOutlined style={{ marginRight: 6 }} />
                    Completion
                  </>
                }
              >
                {timeLogData.taskCompletionPercentage}%
              </Descriptions.Item>
            )}
          {timeLogData.approvalRequired !== undefined && (
            <Descriptions.Item
              label={
                <>
                  <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                  Approval Required
                </>
              }
            >
              {timeLogData.approvalRequired && approvalChain.length > 0 ? (
                <ApprovalHierarchyPopover />
              ) : (
                <Tag
                  color={timeLogData.approvalRequired ? "blue" : "default"}
                >
                  {timeLogData.approvalRequired ? "Yes" : "No"}
                </Tag>
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Created
              </>
            }
            span={2}
          >
            {timeLogData.createdAt
              ? dayjs(timeLogData.createdAt).format("DD MMM YYYY, HH:mm")
              : "—"}
            {timeLogData.createdByName && (
              <span style={{ color: "#999", marginLeft: 8 }}>
                by {timeLogData.createdByName}
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Notes */}
      {timeLogData.notes && timeLogData.notes !== "—" && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <FileTextOutlined style={{ marginRight: 6 }} />
              Notes
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <div
            style={{
              whiteSpace: "pre-wrap",
              color: "#262626",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {timeLogData.notes}
          </div>
        </Card>
      )}

      {/* Allocated Resources */}
      {resourceCount > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TeamOutlined style={{ marginRight: 6 }} />
              Allocated Resources ({resourceCount})
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <Table
            rowKey={(r: any, idx) => `${r.timeLogResourceId ?? idx}`}
            dataSource={timeLogData.timeLogResources}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: "max-content" }}
            columns={[
              {
                title: "Labour Type",
                dataIndex: "laborTypeName",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Labour Category",
                dataIndex: "laborCategoryName",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Resource at Site",
                dataIndex: "actualResourceCount",
                align: "center" as const,
                render: (t: any) => t ?? "-",
              },
              {
                title: "Actual Quantity",
                align: "center" as const,
                render: (_: any, r: any) =>
                  r.actualQuantity != null && r.actualQuantity !== ""
                    ? `${r.actualQuantity}${r.uomName ? ` ${r.uomName}` : ""}`
                    : "-",
              },
              {
                title: "Actual Rate",
                dataIndex: "actualRate",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Total Amount",
                align: "right" as const,
                render: (_: any, r: any) =>
                  formatCurrency(
                    r.totalAmount != null
                      ? Number(r.totalAmount)
                      : resourceLineTotal(
                          r.actualResourceCount,
                          r.actualQuantity,
                          r.actualRate
                        )
                  ),
              },
            ]}
          />
          <div
            className="d-flex justify-content-end mt-2"
            style={{ gap: 8, fontWeight: 600, color: "#004293" }}
          >
            <span>Resources Total:</span>
            <span>
              {formatCurrency(
                timeLogData.resourcesTotalAmount != null
                  ? Number(timeLogData.resourcesTotalAmount)
                  : timeLogData.timeLogResources.reduce(
                      (sum: number, r: any) =>
                        sum +
                        (r.totalAmount != null
                          ? Number(r.totalAmount)
                          : resourceLineTotal(
                              r.actualResourceCount,
                              r.actualQuantity,
                              r.actualRate
                            )),
                      0
                    )
              )}
            </span>
          </div>
        </Card>
      )}

      {/* Allocated Tools */}
      {toolCount > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <ToolOutlined style={{ marginRight: 6 }} />
              Allocated Tools ({toolCount})
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <Table
            rowKey={(t: any, idx) => `${t.timeLogToolId ?? idx}`}
            dataSource={timeLogData.timeLogTools}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: "max-content" }}
            expandable={{
              rowExpandable: (t: any) => (t.toolMaterials || []).length > 0,
              expandedRowRender: (t: any) => (
                <Table
                  rowKey={(m: any, idx) => `${m.timeLogToolMaterialId ?? idx}`}
                  dataSource={t.toolMaterials || []}
                  pagination={false}
                  size="small"
                  bordered
                  title={() => "Consumed Materials"}
                  columns={[
                    {
                      title: "Material",
                      align: "center" as const,
                      render: (_: any, m: any) =>
                        materialNameMap[m.materialId] ||
                        (m.materialId ? `Material #${m.materialId}` : "-"),
                    },
                    {
                      title: "Planned Qty",
                      align: "center" as const,
                      render: (_: any, m: any) =>
                        m.plannedQuantity != null && `${m.plannedQuantity}` !== ""
                          ? `${m.plannedQuantity}${
                              m.workUomName ? ` ${m.workUomName}` : ""
                            }`
                          : "-",
                    },
                    {
                      title: "Planned Rate",
                      align: "center" as const,
                      render: (_: any, m: any) =>
                        (m.plannedRate ?? "") !== "" ? m.plannedRate : "-",
                    },
                    {
                      title: "Actual Qty",
                      align: "center" as const,
                      render: (_: any, m: any) =>
                        m.actualQuantity != null && `${m.actualQuantity}` !== ""
                          ? `${m.actualQuantity}${
                              m.workUomName ? ` ${m.workUomName}` : ""
                            }`
                          : "-",
                    },
                    {
                      title: "Actual Rate",
                      align: "center" as const,
                      render: (_: any, m: any) =>
                        (m.actualRate ?? "") !== "" ? m.actualRate : "-",
                    },
                    {
                      title: "Total Amount",
                      align: "right" as const,
                      render: (_: any, m: any) =>
                        formatCurrency(
                          m.totalAmount != null
                            ? Number(m.totalAmount)
                            : Number(m.actualQuantity || 0) *
                                Number(m.actualRate || 0),
                        ),
                    },
                  ]}
                />
              ),
            }}
            columns={[
              {
                title: "Tool Type",
                dataIndex: "toolTypeName",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Tool/Machinery",
                dataIndex: "toolName",
                align: "center" as const,
                render: (t: any, r: any) =>
                  (t ?? "") !== "" ? t : `Tool #${r.toolId}`,
              },
              {
                title: "Serial Number",
                dataIndex: "serialNumber",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Actual Quantity",
                align: "center" as const,
                render: (_: any, t: any) =>
                  t.actualQuantity != null && t.actualQuantity !== ""
                    ? `${t.actualQuantity}${t.uomName ? ` ${t.uomName}` : ""}`
                    : "-",
              },
              {
                title: "Actual Rate",
                dataIndex: "actualRate",
                align: "center" as const,
                render: (t: any) => ((t ?? "") !== "" ? t : "-"),
              },
              {
                title: "Total Amount",
                align: "right" as const,
                render: (_: any, t: any) =>
                  formatCurrency(
                    t.totalAmount != null
                      ? Number(t.totalAmount)
                      : toolLineTotal(t.actualQuantity, t.actualRate)
                  ),
              },
            ]}
          />
          <div
            className="d-flex justify-content-end mt-2"
            style={{ gap: 8, fontWeight: 600, color: "#004293" }}
          >
            <span>Tools Total:</span>
            <span>
              {formatCurrency(
                timeLogData.toolsTotalAmount != null
                  ? Number(timeLogData.toolsTotalAmount)
                  : timeLogData.timeLogTools.reduce(
                      (sum: number, t: any) =>
                        sum +
                        (t.totalAmount != null
                          ? Number(t.totalAmount)
                          : toolLineTotal(t.actualQuantity, t.actualRate)),
                      0
                    )
              )}
            </span>
          </div>
        </Card>
      )}

      {/* Grand Total across resources + tools */}
      {(resourceCount > 0 || toolCount > 0) && (
        <div
          className="d-flex justify-content-end align-items-center mb-3"
          style={{ gap: 8, fontWeight: 700, color: "#004293", fontSize: 15 }}
        >
          <span>Grand Total:</span>
          <span>
            {formatCurrency(
              timeLogData.totalAmount != null
                ? Number(timeLogData.totalAmount)
                : (timeLogData.timeLogResources || []).reduce(
                    (sum: number, r: any) =>
                      sum +
                      (r.totalAmount != null
                        ? Number(r.totalAmount)
                        : resourceLineTotal(
                            r.actualResourceCount,
                            r.actualQuantity,
                            r.actualRate
                          )),
                    0
                  ) +
                  (timeLogData.timeLogTools || []).reduce(
                    (sum: number, t: any) =>
                      sum +
                      (t.totalAmount != null
                        ? Number(t.totalAmount)
                        : toolLineTotal(t.actualQuantity, t.actualRate)),
                    0
                  )
            )}
          </span>
        </div>
      )}

      {/* Files */}
      {fileCount > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <PaperClipOutlined style={{ marginRight: 6 }} />
              Attachments ({fileCount})
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{
            header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 },
            body: { padding: "12px 16px" },
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {timeLogData.dms.map((f: any, idx: number) => (
              <div
                key={f.docId ?? idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: "#fafafa",
                }}
              >
                <PaperClipOutlined style={{ color: "#722ed1" }} />
                <span style={{ flex: 1, fontSize: 13, color: "#262626" }}>
                  {f.docName || f.fileName || `File ${idx + 1}`}
                </span>
                {f.remarks && (
                  <span style={{ fontSize: 12, color: "#999" }}>
                    {f.remarks}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default TimeLogQuickViewCard;
